import {
	type CoachingAdherenceRepScore,
	MIN_CALLS_FOR_SHARE,
	type Tier,
	formatPercent,
	partlyOrBetterShare,
	shareTier,
} from '../../constants/coachingAdherence';
import type { IScore } from '../../viewModels/gamification';

export type MetricTone = 'green' | 'yellow' | 'red';

export type CoffeeMetricTile =
	| { kind: 'number'; key: string; label: string; value: number; tone: MetricTone }
	| { kind: 'tigers'; key: 'demos'; label: string; demos: number }
	/** A preformatted value; a null tone renders uncoloured (e.g. "n/a"), and `thin` fades a share from too few calls. */
	| { kind: 'share'; key: 'adherence'; label: string; display: string; tone: MetricTone | null; thin: boolean };

export type CoffeePacingBar = {
	label: 'PTS' | 'DIALS';
	current: number;
	goal: number;
	/** Actual progress toward goal, capped at 100 (bar fill width). */
	fillPct: number;
	/** Where the rep should be by now, capped at 100 (pace marker position). */
	markerPct: number;
	tone: MetricTone;
};

/**
 * Raw daily-activity inputs. `score`/`goals`/`projectedScore` are optional
 * because `userDailyActivity` omits them for accounts with no goals configured;
 * `deriveCoffeeMetrics` normalizes missing objects so it never throws.
 */
export type CoffeeMetricsInput = {
	score?: IScore;
	goals?: IScore;
	projectedScore?: IScore;
	isSalesCoffee: boolean;
	/** The user's own adherence row for today; the tile reads n/a while it's absent (loading, failed, or no calls yet). */
	adherence?: CoachingAdherenceRepScore | null;
};

export type CoffeeMetrics = {
	tiles: CoffeeMetricTile[];
	pacingBar: CoffeePacingBar | null;
};

type ScoreKey = 'dials' | 'prospectingDials' | 'connections' | 'demos' | 'points' | 'followUpsSet';

type NormalizedInput = { score: IScore; goals: IScore; projectedScore: IScore; isSalesCoffee: boolean };

const EMPTY_SCORE: IScore = { dials: 0, connections: 0, demos: 0, points: 0 };

/** Percentage of goal reached, matching the legacy gamification viewmodel (0 goal => divide by 1). */
function percentOf(value = 0, goal = 0): number {
	return Math.round((value / (goal || 1)) * 100);
}

/**
 * Tone by pace: actual-vs-projected percentage. Mirrors the legacy
 * `percentToGoal` colouring — >=100 green, >=90 yellow, else red.
 */
function toneFor(input: NormalizedInput, key: ScoreKey): MetricTone {
	const userPct = percentOf(input.score[key], input.goals[key]);
	const goalPct = percentOf(input.projectedScore[key], input.goals[key]);
	const percentToGoal = Math.round((userPct / (goalPct || 1)) * 100);
	return percentToGoal >= 100 ? 'green' : percentToGoal >= 90 ? 'yellow' : 'red';
}

export function deriveCoffeeMetrics(raw: CoffeeMetricsInput): CoffeeMetrics {
	const input: NormalizedInput = {
		score: raw.score ?? EMPTY_SCORE,
		goals: raw.goals ?? EMPTY_SCORE,
		projectedScore: raw.projectedScore ?? EMPTY_SCORE,
		isSalesCoffee: raw.isSalesCoffee,
	};
	const { score, goals, isSalesCoffee } = input;

	const tiles: CoffeeMetricTile[] = [
		{
			kind: 'number',
			key: 'prospectingDials',
			label: 'Prospecting Dials',
			value: score.prospectingDials ?? 0,
			tone: toneFor(input, 'prospectingDials'),
		},
		{ kind: 'number', key: 'dials', label: 'Dials', value: score.dials ?? 0, tone: toneFor(input, 'dials') },
		{
			kind: 'number',
			key: 'connections',
			label: 'Connections',
			value: score.connections ?? 0,
			tone: toneFor(input, 'connections'),
		},
	];

	if ((goals.followUpsSet ?? 0) > 0) {
		tiles.push({
			kind: 'number',
			key: 'followUpsSet',
			label: 'Follow Ups',
			value: score.followUpsSet ?? 0,
			tone: toneFor(input, 'followUpsSet'),
		});
	}

	if (isSalesCoffee) {
		// Legacy has no goal/projection for the cold-demo split, so pace the
		// regular follow-ups tile on the parent follow-ups metric. Clamp at 0 —
		// the API doesn't enforce coldDemoFollowUpsSet <= followUpsSet.
		const regFollowUps = Math.max(0, (score.followUpsSet ?? 0) - (score.coldDemoFollowUpsSet ?? 0));
		tiles.push({
			kind: 'number',
			key: 'regFollowUpsSet',
			label: 'Reg Follow Ups',
			value: regFollowUps,
			tone: toneFor(input, 'followUpsSet'),
		});
	}

	if ((score.demos ?? 0) > 0) {
		tiles.push({ kind: 'tigers', key: 'demos', label: score.demos === 1 ? 'Demo' : 'Demos', demos: score.demos });
	}

	tiles.push(adherenceTile(raw.adherence));

	return { tiles, pacingBar: buildPacingBar(input) };
}

const TIER_TONES: Record<Tier, MetricTone | null> = { good: 'green', warn: 'yellow', bad: 'red', neutral: null };

/** The scoreboard table's "Share partly (30+)", tier and thin-sample fading; "n/a" only with no calls past the hook. */
function adherenceShare(rep?: CoachingAdherenceRepScore | null): {
	display: string;
	tone: MetricTone | null;
	thin: boolean;
} {
	const share = rep ? partlyOrBetterShare(rep) : null;
	return {
		display: formatPercent(share),
		tone: TIER_TONES[shareTier(share)],
		thin: share != null && rep.pastHookCalls < MIN_CALLS_FOR_SHARE,
	};
}

function adherenceTile(rep?: CoachingAdherenceRepScore | null): CoffeeMetricTile {
	return { kind: 'share', key: 'adherence', label: 'Followed Coach', ...adherenceShare(rep) };
}

function buildPacingBar(input: NormalizedInput): CoffeePacingBar | null {
	// Sales-coffee teams pace on prospecting dials; everyone else on points.
	const key: ScoreKey = input.isSalesCoffee ? 'prospectingDials' : 'points';
	const label = input.isSalesCoffee ? 'DIALS' : 'PTS';

	const goal = input.goals[key] ?? 0;
	// No goal configured for the paced metric — hide the bar rather than show 0/0.
	if (goal <= 0) return null;

	return {
		label,
		current: input.score[key] ?? 0,
		goal,
		fillPct: Math.min(percentOf(input.score[key], goal), 100),
		markerPct: Math.min(percentOf(input.projectedScore[key], goal), 100),
		tone: toneFor(input, key),
	};
}
